import { Table, Dropdown } from "@/config/antd";
import CustomMoreOptions from "@/components/specific/Custom_More_Options";
import Column from "antd/es/table/Column";
import { Link } from "react-router-dom";
import "@/assets/styles/custom_table.css";
import PropTypes from "prop-types";
import { useState } from "react";
import InviteUserFormModal from "@/pages/user_management/components/Invite_User_Form_Modal";
import { useSelector } from "react-redux";
import { selectSearchTerm } from "@/store/redux/slices/search/search_slice";
import Avatar from "@/components/common/Avatar";
import { resolveRoleName } from "@/utils/organization_id";

const UserManagementTable = ({
  data,
  setSelectedUsers,
  onDelete,
}) => {
  const [selectedUser, setSelectedUser] = useState(null);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const globalSearchTerm = useSelector(selectSearchTerm) || "";

  if (!data || data.length === 0) {
    return null;
  }

  const transformedData = data.map((user) => ({
    key: user.id,
    id: user.id,
    _id: user._id,
    name: user.fullname || "Unknown User",
    email: user.email || "No email",
    role: resolveRoleName(user.roles) || "User",
    status: user.status || "unknown",
    avatar: user.profileImage || null,
  }));

  // Apply global search filter
  const filteredData = globalSearchTerm
    ? transformedData.filter(
        (item) =>
          item.name?.toLowerCase().includes(globalSearchTerm.toLowerCase()) ||
          item.email?.toLowerCase().includes(globalSearchTerm.toLowerCase()) ||
          item.role?.toLowerCase().includes(globalSearchTerm.toLowerCase()) ||
          item.status?.toLowerCase().includes(globalSearchTerm.toLowerCase())
      )
    : transformedData;

  const rowSelection = {
    onChange: (selectedRowKeys) => {
      setSelectedUsers(selectedRowKeys);
    },
    getCheckboxProps: (record) => ({
      disabled: record.name === "Disabled User",
      name: record.name,
    }),
  };

  const handleMenuClick = ({ key }, record) => {
    if (key === "delete") {
      // Call the parent's delete function with the id so it can find the _id
      onDelete(record.id, record.name);
    } else if (key === "modify") {
      setSelectedUser(record);
      setIsEditModalVisible(true);
    }
  };

  return (
    <div>
      <Table
        rowSelection={{
          type: "checkbox",
          ...rowSelection,
        }}
        dataSource={filteredData}
        pagination={false}
        rowKey={(record) => record.id}
        locale={{ emptyText: " " }}>
        <Column
          title="User"
          key="user"
          dataIndex="name"
          render={(_, record) => (
            <Link
              to={`/dashboard/users/${record._id}`}
              className="flex items-center gap-5 text-sm font-bold">
              <Avatar src={record.avatar} alt={record.name} size={30} />
              <p className="text-regular font-bold">{record.name}</p>
            </Link>
          )}
        />
        <Column
          title="Email Address"
          key="email"
          dataIndex="email"
          render={(_, record) => (
            <Link
              to={`/dashboard/users/${record._id}`}
              className="flex items-center gap-5 text-sm font-bold">
              <p className="text-small font-bold">{record.email}</p>
            </Link>
          )}
        />
        <Column
          title="Roles"
          key="role"
          dataIndex="role"
          render={(_, record) => (
            <Link
              to={`/dashboard/users/${record._id}`}
              className="flex items-center gap-5 text-sm font-bold">
              <p className="text-small font-bold">{record.role}</p>
            </Link>
          )}
        />
        <Column
          title="Status"
          key="status"
          dataIndex="status"
          render={(_, record) => (
            <Link
              to={`/dashboard/users/${record._id}`}
              className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-full bg-primary" />
              <p className="text-primary text-small font-bold">
                {record.status}
              </p>
            </Link>
          )}
        />
        <Column
          title=""
          key="options"
          dataIndex="options"
          render={(_, record) => (
            <Dropdown
              menu={{
                items: [
                  {
                    key: "modify",
                    label: "Modify",
                  },
                  {
                    key: "delete",
                    label: "Delete",
                  },
                ],
                onClick: (e) => handleMenuClick(e, record),
              }}
              trigger={["click"]}>
              <CustomMoreOptions
                variant="filled"
                onClick={(e) => e.stopPropagation()}
                color="black"
                orientation="vertical"
                size="small"
              />
            </Dropdown>
          )}
        />
      </Table>

      <InviteUserFormModal
        isVisible={isEditModalVisible}
        onClose={() => setIsEditModalVisible(false)}
        onUserCreated={() => {
          setIsEditModalVisible(false);
          // Optionally refresh the table data here
        }}
        userData={selectedUser}
        isEditMode={true}
      />
    </div>
  );
};

UserManagementTable.propTypes = {
  data: PropTypes.array,
  setSelectedUsers: PropTypes.func,
  onDelete: PropTypes.func,
};

export default UserManagementTable;
